/**
 * fa_12 — Social History (14th Ave new-student intake) parent fields.
 *
 * Rebuilds the previous flat 8-textarea blob into the full ~100-field
 * paper-form structure: 7 collapsible sections (I-VII) submitted as a
 * single nested JSON payload under PacketFormStatus.form_data.
 *
 * Read path (initialFa12FieldsFromPacket): accept both the new nested
 * shape AND the legacy flat shape (so packets already partially filled
 * out under the old schema still hydrate). Write path: always emit the
 * nested shape. apply_fa12 normalizes either direction and stores
 * nested on StudentSocialHistory.data.
 *
 * Section structure follows fa_12_social_history.py headings after the
 * 2026-05-13 rebuild: I Identification, II History, III Medical,
 * IV Self Help (incl. Feeding sub-block), V Communication (split out
 * to match paper form), VI Skills, VII Emotional Behavior.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FA12_YESNO_NA_ALLOWED_KEYS, FA12_REQUIRED_YESNO_KEYS, FA12_REQUIRED_TEXT_FIELDS, packetFieldId } from './parentPacketValidation';

const FA12_REQUIRED_TEXT_KEYS = new Set(FA12_REQUIRED_TEXT_FIELDS.map(([key]) => key));
const FA12_REQUIRED_YESNO_SET = new Set(FA12_REQUIRED_YESNO_KEYS);

const GARMENTS = [
  { key: 'coat', label: 'Coat' },
  { key: 'shirt', label: 'Shirt' },
  { key: 'pants', label: 'Pants' },
  { key: 'underwear', label: 'Underwear' },
  { key: 'socks', label: 'Socks' },
  { key: 'shoes', label: 'Shoes' },
];

const FASTENERS = [
  { key: 'does_zip', label: 'Zip' },
  { key: 'does_button', label: 'Button' },
  { key: 'does_lace', label: 'Lace' },
  { key: 'does_tie', label: 'Tie' },
  { key: 'does_snap', label: 'Snap' },
];

const CLEANLINESS = [
  { key: 'washes_face', label: 'Washes face' },
  { key: 'washes_hands', label: 'Washes hands' },
  { key: 'takes_bath', label: 'Takes bath / shower' },
  { key: 'brushes_teeth', label: 'Brushes teeth' },
  { key: 'brushes_hair', label: 'Brushes hair' },
];

const CONDITIONS = [
  { key: 'allergies', label: 'Allergies' },
  { key: 'diabetes', label: 'Diabetes' },
  { key: 'sucking', label: 'Sucking difficulty' },
  { key: 'choking', label: 'Choking' },
  { key: 'weight_loss', label: 'Weight loss' },
  { key: 'dehydration', label: 'Dehydration' },
  { key: 'cleft_palate', label: 'Cleft palate' },
  { key: 'breathing', label: 'Breathing difficulty' },
  { key: 'cardiac', label: 'Cardiac problems' },
  { key: 'nasal_regurg', label: 'Nasal regurgitation' },
  { key: 'middle_ear', label: 'Middle ear infection' },
  { key: 'upper_resp', label: 'Upper respiratory infection' },
];

const FEEDING_BEHAVIOR = [
  { key: 'refuses_food', label: 'My child refuses food.' },
  { key: 'vomits_after', label: 'My child vomits after feeding.' },
  { key: 'vomits_during', label: 'My child vomits during feeding.' },
  { key: 'long_time', label: 'My child takes a long time to eat.' },
  { key: 'liquids_difficulty', label: 'My child has difficulty drinking liquids.' },
  { key: 'solids_difficulty', label: 'My child has difficulty eating solids.' },
  { key: 'food_spills', label: 'Food spills out of my child\u2019s mouth.' },
  { key: 'cries_during', label: 'My child cries during mealtime.' },
  { key: 'cries_after', label: 'My child cries after mealtime.' },
  { key: 'force_fed', label: 'My child needs to be force-fed.' },
  { key: 'drools', label: 'My child drools frequently.' },
  { key: 'coughs', label: 'My child coughs when eating.' },
  { key: 'chokes', label: 'My child chokes when eating.' },
];

const DRINKING = [
  { key: 'drink_bottle', label: 'Bottle' },
  { key: 'drink_cup', label: 'Cup' },
  { key: 'drink_straw', label: 'Straw' },
  { key: 'drink_other', label: 'Other' },
];

const EATING = [
  { key: 'eat_fingers', label: 'Uses fingers' },
  { key: 'eat_spoon', label: 'Spoon' },
  { key: 'eat_fork', label: 'Fork' },
  { key: 'eat_chews_solid', label: 'Chews solid food' },
  { key: 'eat_assisted', label: 'Eats assisted' },
  { key: 'eat_unassisted', label: 'Eats unassisted' },
];

const COMM_METHODS = [
  { key: 'comm_verbally', label: 'Verbally' },
  { key: 'comm_gestures', label: 'Gestures' },
];

const COMM_YN = [
  { prefix: 'phrases', label: 'Speaks using phrases?' },
  { prefix: 'sentences', label: 'Speaks using sentences?' },
  { prefix: 'directions', label: 'Follows simple directions?' },
  { prefix: 'name', label: 'Responds to name?' },
  { prefix: 'sign', label: 'Uses sign language?' },
];

const PLAY_HABITS = [
  { prefix: 'occupies_self', label: 'Occupies self unattended' },
  { prefix: 'plays_other_children', label: 'Plays with other children' },
  { prefix: 'plays_adults', label: 'Plays with adults' },
  { prefix: 'plays_toys', label: 'Plays appropriately with toys (board games, computer games, etc.)' },
];

const EMOTIONAL = [
  { key: 'emo_unaware', label: 'Unaware' },
  { key: 'emo_tantrums', label: 'Tantrums' },
  { key: 'emo_bites_himself', label: 'Bites himself' },
  { key: 'emo_bangs_head', label: 'Bangs head' },
  { key: 'emo_passive', label: 'Passive' },
  { key: 'emo_stubborn', label: 'Stubborn' },
  { key: 'emo_destructive', label: 'Destructive' },
  { key: 'emo_hits_himself', label: 'Hits himself' },
  { key: 'emo_shy', label: 'Shy' },
  { key: 'emo_bites_others', label: 'Bites others' },
  { key: 'emo_hyperactive', label: 'Hyperactive' },
  { key: 'emo_easily_upset', label: 'Easily upset' },
  { key: 'emo_hits_others', label: 'Hits others' },
  { key: 'emo_kicks_others', label: 'Kicks others' },
  { key: 'emo_short_attention', label: 'Short attention span' },
  { key: 'emo_jealous', label: 'Jealous' },
  { key: 'emo_happy', label: 'Happy' },
  { key: 'emo_smiles', label: 'Smiles' },
  { key: 'emo_spits', label: 'Spits' },
  { key: 'emo_smiles_socially', label: 'Smiles socially' },
];

const MED_SLOTS = [0, 1, 2];

// --------------------------------------------------------------------------
// Read helpers — read new nested first, fall back to legacy flat keys so
// packets started under the old schema still hydrate. The PDF generator
// uses the same read order.
// --------------------------------------------------------------------------

function pickStr(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v);
    if (s) return s;
  }
  return '';
}

function pickBool(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    return !!v;
  }
  return false;
}

/**
 * Build the initial fa_12 fields object for the parent packet UI from
 * the StudentSocialHistory.data JSON returned by the API (already in
 * either nested or legacy-flat shape).
 *
 * Read order per field: new nested first, then legacy flat. This means
 * a packet that's been partially filled out by a parent under the old
 * 8-textarea schema will still light up under the new accordion UI in
 * the relevant places, and the legacy summary fields stay reachable as
 * Section VII "Other notes" if needed.
 */
export function initialFa12FieldsFromPacket(sh) {
  const d = sh && typeof sh === 'object' ? sh : {};
  const ident = (d.identification && typeof d.identification === 'object') ? d.identification : {};
  const hist = (d.history && typeof d.history === 'object') ? d.history : {};
  const med = (d.medical && typeof d.medical === 'object') ? d.medical : {};
  const meds = Array.isArray(med.medications) ? med.medications : [];
  const conditions = (med.conditions && typeof med.conditions === 'object') ? med.conditions : {};
  const sh4 = (d.self_help && typeof d.self_help === 'object') ? d.self_help : {};
  const feeding = (sh4.feeding && typeof sh4.feeding === 'object') ? sh4.feeding : (d.feeding || {});
  const comm = (d.communication && typeof d.communication === 'object') ? d.communication : {};
  const skills = (d.skills && typeof d.skills === 'object') ? d.skills : {};
  const emo = (d.emotional && typeof d.emotional === 'object') ? d.emotional : {};

  const out = {
    // Header
    date: pickStr(d.date),
    person_completing: pickStr(d.person_completing),

    // Section I — Identification
    sex: pickStr(ident.sex, d.sex),
    address: pickStr(ident.address, d.address),
    apt: pickStr(ident.apt, d.apt),
    zip: pickStr(ident.zip, d.zip),
    phone: pickStr(ident.phone, d.phone),
    agency_name: pickStr(ident.agency_name, d.agency_name),
    social_worker: pickStr(ident.social_worker, d.social_worker),
    household_members: pickStr(ident.household_members, d.household_members),
    emergency_name: pickStr(ident.emergency_name, d.emergency_name),
    emergency_relationship: pickStr(ident.emergency_relationship, d.emergency_relationship),
    emergency_address: pickStr(ident.emergency_address, d.emergency_address),
    emergency_phone: pickStr(ident.emergency_phone, d.emergency_phone),
    language_home: pickStr(ident.language_home, d.language_home),

    // Section II — History
    prev_school_name: pickStr(hist.prev_school_name, d.prev_school_name),
    prev_school_phone: pickStr(hist.prev_school_phone, d.prev_school_phone),
    prev_contact_person: pickStr(hist.prev_contact_person, d.prev_contact_person),

    // Section III — Medical
    doctor_facility: pickStr(med.doctor_facility, d.doctor_facility),
    doctor_phone: pickStr(med.doctor_phone, d.doctor_phone),
    last_visit_date: pickStr(med.last_visit_date, d.last_visit_date),
    clinic_social_worker: pickStr(med.clinic_social_worker, d.clinic_social_worker),

    seizures_yn: pickBool(med.seizures_yes, d.seizures_yes)
      ? 'yes'
      : pickBool(med.seizures_no, d.seizures_no) ? 'no' : '',
    seizures_type: pickStr(med.seizures_type, d.seizures_type),
    seizures_signs_onset: pickStr(med.seizures_signs_onset, d.seizures_signs_onset),
    seizures_pattern: pickStr(med.seizures_pattern, d.seizures_pattern),

    hospitalization_reason: pickStr(med.hospitalization_reason, d.hospitalization_reason),
    handicaps_other: pickStr(med.handicaps_other, d.handicaps_other),
    prev_medical_clinic: pickStr(med.prev_medical_clinic, d.prev_medical_clinic),
    prev_medical_location: pickStr(med.prev_medical_location, d.prev_medical_location),
    prev_medical_dates: pickStr(med.prev_medical_dates, d.prev_medical_dates),

    infection_frequency_severity: pickStr(med.infection_frequency_severity, d.infection_frequency_severity),

    // Section IV — Self Help
    toileting_yn: pickBool(sh4.toileting_unassisted, d.toileting_unassisted)
      ? 'unassisted'
      : pickBool(sh4.toileting_assisted, d.toileting_assisted) ? 'assisted' : '',

    dressing_other: pickStr(sh4.dressing_other, d.dressing_other),
    cleanliness_other: pickStr(sh4.cleanliness_other, d.cleanliness_other),

    // Sleep habits
    bedtime: pickStr(sh4.bedtime, d.bedtime),
    naps_yn: pickBool(sh4.naps_yes, d.naps_yes) ? 'yes'
      : pickBool(sh4.naps_no, d.naps_no) ? 'no' : '',
    sleeps_through_yn: pickBool(sh4.sleeps_through_yes, d.sleeps_through_yes) ? 'yes'
      : pickBool(sh4.sleeps_through_no, d.sleeps_through_no) ? 'no' : '',
    where_sleeps: pickStr(sh4.where_sleeps, d.where_sleeps),
    with_whom: pickStr(sh4.with_whom, d.with_whom),
    toilet_accidents_yn: pickBool(sh4.toilet_accidents_yes, d.toilet_accidents_yes) ? 'yes'
      : pickBool(sh4.toilet_accidents_no, d.toilet_accidents_no) ? 'no' : '',
    nightmares: pickStr(sh4.nightmares, d.nightmares),

    // Feeding (nested under self_help.feeding in new shape)
    feeding_difficulties: pickStr(feeding.feeding_difficulties, d.feeding_difficulties),
    behavior_comments: pickStr(feeding.behavior_comments, d.behavior_comments),
    drink_other_specify: pickStr(feeding.drink_other_specify, d.drink_other_specify),
    allergic_foods: pickStr(feeding.allergic_foods, d.allergic_foods),
    disliked_foods: pickStr(feeding.disliked_foods, d.disliked_foods),
    preferred_foods: pickStr(feeding.preferred_foods, d.preferred_foods),
    restricted_foods: pickStr(feeding.restricted_foods, d.restricted_foods),
    food_consistency: pickStr(feeding.food_consistency, d.food_consistency),
    easiest_foods: pickStr(feeding.easiest_foods, d.easiest_foods),
    hardest_foods: pickStr(feeding.hardest_foods, d.hardest_foods),

    // Section V — Communication
    rsa_from_whom: pickStr(comm.rsa_from_whom, d.rsa_from_whom),
    rsa_how_often: pickStr(comm.rsa_how_often, d.rsa_how_often),

    // Section VI — Skills
    leisure: pickStr(skills.leisure, d.leisure),
    fears: pickStr(skills.fears, d.fears),
    goes_outside_yn: pickBool(skills.goes_outside_yes, d.goes_outside_yes) ? 'yes'
      : pickBool(skills.goes_outside_no, d.goes_outside_no) ? 'no' : '',
    favorite_activities: pickStr(skills.favorite_activities, d.favorite_activities),

    // Section VII — Emotional Behavior
    seek_attention_yn: pickBool(emo.seek_attention_yes, d.seek_attention_yes) ? 'yes'
      : pickBool(emo.seek_attention_no, d.seek_attention_no) ? 'no' : '',
    how_seek: pickStr(emo.how_seek, d.how_seek),
    what_calms: pickStr(emo.what_calms, d.what_calms),
    share_attention_yn: pickBool(emo.share_attention_yes, d.share_attention_yes) ? 'yes'
      : pickBool(emo.share_attention_no, d.share_attention_no) ? 'no' : '',
    react_transitions: pickStr(emo.react_transitions, d.react_transitions),
    react_new_env: pickStr(emo.react_new_env, d.react_new_env),
  };

  // Medications: 3 fixed slots, hydrate from `medical.medications[]` first then legacy flat med_* keys.
  MED_SLOTS.forEach((i) => {
    const slot = meds[i] && typeof meds[i] === 'object' ? meds[i] : {};
    const legacyName = (i === 0 ? pickStr(d.med_name) : '');
    const legacyDate = (i === 0 ? pickStr(d.med_date_prescribed) : '');
    const legacyReason = (i === 0 ? pickStr(d.med_reason) : '');
    const legacyDosage = (i === 0 ? pickStr(d.med_dosage) : '');
    const legacyTime = (i === 0 ? pickStr(d.med_time) : '');
    const legacyLastEval = (i === 0 ? pickStr(d.med_last_evaluated) : '');
    out[`med_${i}_name`] = pickStr(slot.name, legacyName);
    out[`med_${i}_date_prescribed`] = pickStr(slot.date_prescribed, legacyDate);
    out[`med_${i}_reason`] = pickStr(slot.reason, legacyReason);
    out[`med_${i}_dosage`] = pickStr(slot.dosage, legacyDosage);
    out[`med_${i}_time`] = pickStr(slot.time, legacyTime);
    out[`med_${i}_last_evaluated`] = pickStr(slot.last_evaluated, legacyLastEval);
  });

  // Conditions Now/Past grid
  CONDITIONS.forEach(({ key }) => {
    const cond = (conditions[key] && typeof conditions[key] === 'object') ? conditions[key] : {};
    out[`cond_${key}_now`] = pickBool(cond.now, d[`${key}_now`]);
    out[`cond_${key}_past`] = pickBool(cond.past, d[`${key}_past`]);
  });

  // Dressing checkboxes
  GARMENTS.forEach(({ key }) => {
    out[`takes_off_${key}`] = pickBool(sh4[`takes_off_${key}`], d[`takes_off_${key}`]);
    out[`puts_on_${key}`] = pickBool(sh4[`puts_on_${key}`], d[`puts_on_${key}`]);
  });
  FASTENERS.forEach(({ key }) => {
    out[key] = pickBool(sh4[key], d[key]);
  });

  // Cleanliness (these are free-text frequencies, not booleans)
  CLEANLINESS.forEach(({ key }) => {
    out[key] = pickStr(sh4[key], d[key]);
  });

  // Feeding behavior Y/N
  FEEDING_BEHAVIOR.forEach(({ key }) => {
    out[`feed_${key}_yn`] = pickBool(feeding[`${key}_yes`], d[`${key}_yes`])
      ? 'yes'
      : pickBool(feeding[`${key}_no`], d[`${key}_no`]) ? 'no' : '';
  });

  // Drinking + eating checkboxes
  DRINKING.forEach(({ key }) => {
    out[key] = pickBool(feeding[key], d[key]);
  });
  EATING.forEach(({ key }) => {
    out[key] = pickBool(feeding[key], d[key]);
  });

  // Communication checkboxes + Y/N
  COMM_METHODS.forEach(({ key }) => {
    out[key] = pickBool(comm[key], d[key]);
  });
  COMM_YN.forEach(({ prefix }) => {
    out[`${prefix}_yn`] = pickBool(comm[`${prefix}_yes`], d[`${prefix}_yes`])
      ? 'yes'
      : pickBool(comm[`${prefix}_no`], d[`${prefix}_no`]) ? 'no' : '';
  });

  // Skills play habits Y/N
  PLAY_HABITS.forEach(({ prefix }) => {
    out[`${prefix}_yn`] = pickBool(skills[`${prefix}_yes`], d[`${prefix}_yes`])
      ? 'yes'
      : pickBool(skills[`${prefix}_no`], d[`${prefix}_no`]) ? 'no' : '';
  });

  // Emotional behavior checkboxes
  EMOTIONAL.forEach(({ key }) => {
    out[key] = pickBool(emo[key], d[key]);
  });

  return out;
}

/**
 * Serialize fa_12 fields back into the nested JSON payload submitted to
 * the backend. Matches the schema apply_fa12 stores on
 * ``StudentSocialHistory.data``.
 */
export function buildFa12SubmitPayload(fields) {
  const f = fields || {};
  const ynBools = (val) => ({
    yes: val === 'yes',
    no: val === 'no',
  });

  const medications = MED_SLOTS.map((i) => ({
    name: f[`med_${i}_name`] || '',
    date_prescribed: f[`med_${i}_date_prescribed`] || '',
    reason: f[`med_${i}_reason`] || '',
    dosage: f[`med_${i}_dosage`] || '',
    time: f[`med_${i}_time`] || '',
    last_evaluated: f[`med_${i}_last_evaluated`] || '',
  }));

  const conditions = {};
  CONDITIONS.forEach(({ key }) => {
    conditions[key] = {
      now: !!f[`cond_${key}_now`],
      past: !!f[`cond_${key}_past`],
    };
  });

  const seizures = ynBools(f.seizures_yn);
  const naps = ynBools(f.naps_yn);
  const sleepsThrough = ynBools(f.sleeps_through_yn);
  const toiletAccidents = ynBools(f.toilet_accidents_yn);
  const goesOutside = ynBools(f.goes_outside_yn);
  const seekAttention = ynBools(f.seek_attention_yn);
  const shareAttention = ynBools(f.share_attention_yn);

  const selfHelp = {
    toileting_assisted: f.toileting_yn === 'assisted',
    toileting_unassisted: f.toileting_yn === 'unassisted',
    dressing_other: f.dressing_other || '',
    cleanliness_other: f.cleanliness_other || '',
    bedtime: f.bedtime || '',
    naps_yes: naps.yes,
    naps_no: naps.no,
    sleeps_through_yes: sleepsThrough.yes,
    sleeps_through_no: sleepsThrough.no,
    where_sleeps: f.where_sleeps || '',
    with_whom: f.with_whom || '',
    toilet_accidents_yes: toiletAccidents.yes,
    toilet_accidents_no: toiletAccidents.no,
    nightmares: f.nightmares || '',
  };
  GARMENTS.forEach(({ key }) => {
    selfHelp[`takes_off_${key}`] = !!f[`takes_off_${key}`];
    selfHelp[`puts_on_${key}`] = !!f[`puts_on_${key}`];
  });
  FASTENERS.forEach(({ key }) => {
    selfHelp[key] = !!f[key];
  });
  CLEANLINESS.forEach(({ key }) => {
    selfHelp[key] = f[key] || '';
  });

  const feeding = {
    feeding_difficulties: f.feeding_difficulties || '',
    behavior_comments: f.behavior_comments || '',
    drink_other_specify: f.drink_other_specify || '',
    allergic_foods: f.allergic_foods || '',
    disliked_foods: f.disliked_foods || '',
    preferred_foods: f.preferred_foods || '',
    restricted_foods: f.restricted_foods || '',
    food_consistency: f.food_consistency || '',
    easiest_foods: f.easiest_foods || '',
    hardest_foods: f.hardest_foods || '',
  };
  FEEDING_BEHAVIOR.forEach(({ key }) => {
    const yn = ynBools(f[`feed_${key}_yn`]);
    feeding[`${key}_yes`] = yn.yes;
    feeding[`${key}_no`] = yn.no;
  });
  DRINKING.forEach(({ key }) => { feeding[key] = !!f[key]; });
  EATING.forEach(({ key }) => { feeding[key] = !!f[key]; });
  selfHelp.feeding = feeding;

  const communication = {
    rsa_from_whom: f.rsa_from_whom || '',
    rsa_how_often: f.rsa_how_often || '',
  };
  COMM_METHODS.forEach(({ key }) => { communication[key] = !!f[key]; });
  COMM_YN.forEach(({ prefix }) => {
    const yn = ynBools(f[`${prefix}_yn`]);
    communication[`${prefix}_yes`] = yn.yes;
    communication[`${prefix}_no`] = yn.no;
  });

  const skills = {
    leisure: f.leisure || '',
    fears: f.fears || '',
    goes_outside_yes: goesOutside.yes,
    goes_outside_no: goesOutside.no,
    favorite_activities: f.favorite_activities || '',
  };
  PLAY_HABITS.forEach(({ prefix }) => {
    const yn = ynBools(f[`${prefix}_yn`]);
    skills[`${prefix}_yes`] = yn.yes;
    skills[`${prefix}_no`] = yn.no;
  });

  const emotional = {
    seek_attention_yes: seekAttention.yes,
    seek_attention_no: seekAttention.no,
    how_seek: f.how_seek || '',
    what_calms: f.what_calms || '',
    share_attention_yes: shareAttention.yes,
    share_attention_no: shareAttention.no,
    react_transitions: f.react_transitions || '',
    react_new_env: f.react_new_env || '',
  };
  EMOTIONAL.forEach(({ key }) => { emotional[key] = !!f[key]; });

  return {
    date: f.date || '',
    person_completing: f.person_completing || '',
    identification: {
      sex: f.sex || '',
      address: f.address || '',
      apt: f.apt || '',
      zip: f.zip || '',
      phone: f.phone || '',
      agency_name: f.agency_name || '',
      social_worker: f.social_worker || '',
      household_members: f.household_members || '',
      emergency_name: f.emergency_name || '',
      emergency_relationship: f.emergency_relationship || '',
      emergency_address: f.emergency_address || '',
      emergency_phone: f.emergency_phone || '',
      language_home: f.language_home || '',
    },
    history: {
      prev_school_name: f.prev_school_name || '',
      prev_school_phone: f.prev_school_phone || '',
      prev_contact_person: f.prev_contact_person || '',
    },
    medical: {
      doctor_facility: f.doctor_facility || '',
      doctor_phone: f.doctor_phone || '',
      last_visit_date: f.last_visit_date || '',
      clinic_social_worker: f.clinic_social_worker || '',
      medications,
      seizures_yes: seizures.yes,
      seizures_no: seizures.no,
      seizures_type: f.seizures_type || '',
      seizures_signs_onset: f.seizures_signs_onset || '',
      seizures_pattern: f.seizures_pattern || '',
      hospitalization_reason: f.hospitalization_reason || '',
      handicaps_other: f.handicaps_other || '',
      prev_medical_clinic: f.prev_medical_clinic || '',
      prev_medical_location: f.prev_medical_location || '',
      prev_medical_dates: f.prev_medical_dates || '',
      conditions,
      infection_frequency_severity: f.infection_frequency_severity || '',
    },
    self_help: selfHelp,
    communication,
    skills,
    emotional,
  };
}

// --------------------------------------------------------------------------
// Component-local UI primitives. Kept inside this file so fa_12 layout
// changes don't leak into other forms.
// --------------------------------------------------------------------------

const Fa12FieldContext = createContext({ fieldErrors: {}, clearFieldError: null });

function TextRow({
  label, k, fields, setF, multiline = false, minRows = 2,
}) {
  const { fieldErrors, clearFieldError } = useContext(Fa12FieldContext);
  const errorMsg = fieldErrors[k];
  const isRequired = FA12_REQUIRED_TEXT_KEYS.has(k);
  return (
    <TextField
      fullWidth
      size="small"
      margin="dense"
      id={packetFieldId(k)}
      label={label}
      required={isRequired}
      value={fields[k] ?? ''}
      onChange={(e) => {
        if (clearFieldError) clearFieldError(k);
        setF(k, e.target.value);
      }}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      error={!!errorMsg}
      helperText={errorMsg || undefined}
      inputProps={{ 'data-field': k }}
    />
  );
}

function YesNoRow({
  label, k, fields, setF, fieldErrors = {}, clearFieldError, readOnly = false,
}) {
  const errorMsg = fieldErrors[k];
  const fieldId = packetFieldId(k);
  const labelId = `${fieldId}-label`;
  const showNa = FA12_YESNO_NA_ALLOWED_KEYS.has(k);
  const isRequired = FA12_REQUIRED_YESNO_SET.has(k);
  const reqStar = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
  );

  const handleChange = (e) => {
    if (clearFieldError) clearFieldError(k);
    setF(k, e.target.value);
  };

  return (
    <Box
      id={fieldId}
      component="fieldset"
      sx={{
        display: 'block',
        border: 'none',
        p: 0,
        m: 0,
        minWidth: 0,
        mb: 0.5,
        ...(errorMsg
          ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 }
          : {}),
      }}
    >
      <FormLabel
        id={labelId}
        component="legend"
        sx={{ fontSize: 14, mb: 0.5, display: 'block' }}
        required={isRequired}
      >
        {label}{isRequired ? reqStar : null}
      </FormLabel>
      <RadioGroup
        row
        name={fieldId}
        value={fields[k] ?? ''}
        onChange={handleChange}
        aria-labelledby={labelId}
        aria-invalid={errorMsg ? 'true' : undefined}
      >
        <FormControlLabel
          value="yes"
          control={(
            <Radio size="small" disabled={readOnly} color={errorMsg ? 'error' : undefined} />
          )}
          label="Yes"
        />
        <FormControlLabel
          value="no"
          control={(
            <Radio size="small" disabled={readOnly} color={errorMsg ? 'error' : undefined} />
          )}
          label="No"
        />
        {showNa && (
          <FormControlLabel
            value="na"
            control={(
              <Radio size="small" disabled={readOnly} color={errorMsg ? 'error' : undefined} />
            )}
            label="N/A"
          />
        )}
      </RadioGroup>
      {errorMsg && (
        <Typography variant="caption" color="error" component="div" role="alert">
          {errorMsg}
        </Typography>
      )}
    </Box>
  );
}

function CheckboxGroup({ items, fields, setF, cols = 3 }) {
  return (
    <Grid container spacing={0}>
      {items.map(({ key, label }) => (
        <Grid item xs={12} sm={12 / Math.min(cols, 2)} md={12 / cols} key={key}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!fields[key]}
                onChange={(e) => setF(key, e.target.checked)}
              />
            }
            label={label}
          />
        </Grid>
      ))}
    </Grid>
  );
}

function TwoCol({ children }) {
  return (
    <Grid container spacing={1}>
      {React.Children.map(children, (child, i) => (
        <Grid item xs={12} sm={6} key={i}>
          {child}
        </Grid>
      ))}
    </Grid>
  );
}

const FA12_SECTION_IDS = [
  'identification',
  'history',
  'medical',
  'self_help',
  'communication',
  'skills',
  'emotional',
];

const FA12_INITIAL_EXPANDED = {
  identification: true,
  history: false,
  medical: false,
  self_help: false,
  communication: false,
  skills: false,
  emotional: false,
};

function SectionAccordion({
  title, sectionId, expanded, onExpandedChange, children,
}) {
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(sectionId, isExpanded)}
      disableGutters
      sx={{ mb: 0.5 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

// --------------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------------

export default function Fa12SocialHistoryFields({
  fields, setF, packet, fieldErrors = {}, clearFieldError, readOnly = false,
}) {
  const childName = `${packet?.student_first_name || ''} ${packet?.student_last_name || ''}`.trim() || '—';
  const childDob = packet?.student_dob || '—';
  const [expandedSections, setExpandedSections] = useState(FA12_INITIAL_EXPANDED);

  useEffect(() => {
    if (Object.keys(fieldErrors || {}).length > 0) {
      setExpandedSections(
        FA12_SECTION_IDS.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      );
    }
  }, [fieldErrors]);

  const handleSectionExpanded = useCallback((sectionId, isExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: isExpanded }));
  }, []);

  return (
    <Fa12FieldContext.Provider value={{ fieldErrors, clearFieldError }}>
    <Box>
      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Social History (new students)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Child: {childName} &middot; DOB: {childDob}
      </Typography>

      <TwoCol>
        <TextRow label="Date" k="date" fields={fields} setF={setF} />
        <TextRow label="Person completing form" k="person_completing" fields={fields} setF={setF} />
      </TwoCol>

      <SectionAccordion
        title="I. Identification"
        sectionId="identification"
        expanded={!!expandedSections.identification}
        onExpandedChange={handleSectionExpanded}
      >
        <TwoCol>
          <FormControl fullWidth margin="dense" size="small" required error={!!fieldErrors.sex}>
            <InputLabel id="fa12-sex-label">Sex (M / F)</InputLabel>
            <Select
              labelId="fa12-sex-label"
              id={packetFieldId('sex')}
              label="Sex (M / F)"
              value={fields.sex ?? ''}
              onChange={(e) => {
                setF('sex', e.target.value);
                if (clearFieldError) clearFieldError('sex');
              }}
              inputProps={{ 'data-field': 'sex' }}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="F">F</MenuItem>
            </Select>
            {!!fieldErrors.sex && (
              <FormHelperText error>{fieldErrors.sex}</FormHelperText>
            )}
          </FormControl>
          <TextRow label="Phone" k="phone" fields={fields} setF={setF} />
        </TwoCol>
        <TwoCol>
          <TextRow label="Address" k="address" fields={fields} setF={setF} />
          <TextRow label="Apt #" k="apt" fields={fields} setF={setF} />
        </TwoCol>
        <TwoCol>
          <TextRow label="Zip" k="zip" fields={fields} setF={setF} />
          <TextRow label="Agency" k="agency_name" fields={fields} setF={setF} />
        </TwoCol>
        <TextRow label="Social worker" k="social_worker" fields={fields} setF={setF} />
        <TextRow
          label="Other household members (grandparents, aunts, etc.)"
          k="household_members" fields={fields} setF={setF}
          multiline
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact</Typography>
        <TwoCol>
          <TextRow label="Name" k="emergency_name" fields={fields} setF={setF} />
          <TextRow label="Relationship" k="emergency_relationship" fields={fields} setF={setF} />
        </TwoCol>
        <TwoCol>
          <TextRow label="Address" k="emergency_address" fields={fields} setF={setF} />
          <TextRow label="Phone" k="emergency_phone" fields={fields} setF={setF} />
        </TwoCol>

        <TextRow label="Language spoken at home" k="language_home" fields={fields} setF={setF} />
      </SectionAccordion>

      <SectionAccordion
        title="II. History — Previous schooling"
        sectionId="history"
        expanded={!!expandedSections.history}
        onExpandedChange={handleSectionExpanded}
      >
        <TextRow label="Name of school" k="prev_school_name" fields={fields} setF={setF} />
        <TwoCol>
          <TextRow label="Phone" k="prev_school_phone" fields={fields} setF={setF} />
          <TextRow
            label="Contact person (teacher, psychologist, social worker)"
            k="prev_contact_person" fields={fields} setF={setF}
          />
        </TwoCol>
      </SectionAccordion>

      <SectionAccordion
        title="III. Medical Information"
        sectionId="medical"
        expanded={!!expandedSections.medical}
        onExpandedChange={handleSectionExpanded}
      >
        <TextRow
          label="Medical facility or doctor currently familiar with child"
          k="doctor_facility" fields={fields} setF={setF}
        />
        <TwoCol>
          <TextRow label="Phone" k="doctor_phone" fields={fields} setF={setF} />
          <TextRow label="Date of most recent visit" k="last_visit_date" fields={fields} setF={setF} />
        </TwoCol>
        <TextRow
          label="Name of social worker and/or doctor at clinic"
          k="clinic_social_worker" fields={fields} setF={setF}
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Medications (up to 3)</Typography>
        {MED_SLOTS.map((i) => (
          <Box key={`med-${i}`} sx={{ p: 1, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Medication {i + 1}
            </Typography>
            <TwoCol>
              <TextRow label="Name of medication" k={`med_${i}_name`} fields={fields} setF={setF} />
              <TextRow label="Date prescribed" k={`med_${i}_date_prescribed`} fields={fields} setF={setF} />
            </TwoCol>
            <TwoCol>
              <TextRow label="Reason for medication" k={`med_${i}_reason`} fields={fields} setF={setF} />
              <TextRow label="Dosage" k={`med_${i}_dosage`} fields={fields} setF={setF} />
            </TwoCol>
            <TwoCol>
              <TextRow label="Time to be taken" k={`med_${i}_time`} fields={fields} setF={setF} />
              <TextRow label="Last evaluated" k={`med_${i}_last_evaluated`} fields={fields} setF={setF} />
            </TwoCol>
          </Box>
        ))}

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Seizures</Typography>
        <YesNoRow label="Does child have seizures?" k="seizures_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <TextRow label="Type" k="seizures_type" fields={fields} setF={setF} />
        <TwoCol>
          <TextRow label="Signs of onset, if any" k="seizures_signs_onset" fields={fields} setF={setF} />
          <TextRow label="Seizure pattern" k="seizures_pattern" fields={fields} setF={setF} />
        </TwoCol>

        <TextRow
          label="Any reason for hospitalization, and how long was the hospitalization?"
          k="hospitalization_reason" fields={fields} setF={setF} multiline
        />
        <TextRow label="Specific handicaps" k="handicaps_other" fields={fields} setF={setF} multiline />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Previous medical associations</Typography>
        <TextRow label="Name of clinic" k="prev_medical_clinic" fields={fields} setF={setF} />
        <TwoCol>
          <TextRow label="Location" k="prev_medical_location" fields={fields} setF={setF} />
          <TextRow label="Dates service received" k="prev_medical_dates" fields={fields} setF={setF} />
        </TwoCol>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Indicate whether your child currently, or in the past, has had any of the following:
        </Typography>
        <Box
          component="table"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            mt: 1,
            '& th, & td': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: 0.75,
              pr: 1,
              verticalAlign: 'middle',
            },
            '& th': { fontWeight: 600, textAlign: 'left' },
            '& th:nth-of-type(2), & th:nth-of-type(3), & td:nth-of-type(2), & td:nth-of-type(3)': {
              width: 72,
              textAlign: 'center',
            },
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box component="th">Condition</Box>
              <Box component="th">Now</Box>
              <Box component="th">Past</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {CONDITIONS.map(({ key, label }) => (
              <Box component="tr" key={key}>
                <Box component="td">
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{label}</Typography>
                </Box>
                <Box component="td">
                  <Checkbox
                    size="small"
                    checked={!!fields[`cond_${key}_now`]}
                    onChange={(e) => setF(`cond_${key}_now`, e.target.checked)}
                    inputProps={{ 'aria-label': `${label} now` }}
                  />
                </Box>
                <Box component="td">
                  <Checkbox
                    size="small"
                    checked={!!fields[`cond_${key}_past`]}
                    onChange={(e) => setF(`cond_${key}_past`, e.target.checked)}
                    inputProps={{ 'aria-label': `${label} past` }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <TextRow
          label="If your child has suffered from upper respiratory or middle ear infection, indicate frequency and severity"
          k="infection_frequency_severity" fields={fields} setF={setF} multiline
        />
      </SectionAccordion>

      <SectionAccordion
        title="IV. Self Help"
        sectionId="self_help"
        expanded={!!expandedSections.self_help}
        onExpandedChange={handleSectionExpanded}
      >
        <Typography variant="subtitle2">Toileting</Typography>
        <FormControl margin="dense" sx={{ display: 'block' }}>
          <RadioGroup
            row
            value={fields.toileting_yn ?? ''}
            onChange={(e) => setF('toileting_yn', e.target.value)}
          >
            <FormControlLabel value="assisted" control={<Radio size="small" />} label="Assisted" />
            <FormControlLabel value="unassisted" control={<Radio size="small" />} label="Unassisted" />
          </RadioGroup>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Dressing</Typography>
        <Typography variant="body2" color="text.secondary">
          Check each garment the child can take off / put on independently.
        </Typography>
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Takes off:</Typography>
            {GARMENTS.map(({ key, label }) => (
              <FormControlLabel
                key={`off-${key}`}
                control={
                  <Checkbox
                    size="small"
                    checked={!!fields[`takes_off_${key}`]}
                    onChange={(e) => setF(`takes_off_${key}`, e.target.checked)}
                  />
                }
                label={label}
                sx={{ display: 'block' }}
              />
            ))}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Puts on:</Typography>
            {GARMENTS.map(({ key, label }) => (
              <FormControlLabel
                key={`on-${key}`}
                control={
                  <Checkbox
                    size="small"
                    checked={!!fields[`puts_on_${key}`]}
                    onChange={(e) => setF(`puts_on_${key}`, e.target.checked)}
                  />
                }
                label={label}
                sx={{ display: 'block' }}
              />
            ))}
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ mt: 1 }}>Does the child:</Typography>
        <CheckboxGroup items={FASTENERS} fields={fields} setF={setF} cols={5} />
        <TextRow label="Other information" k="dressing_other" fields={fields} setF={setF} />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Cleanliness</Typography>
        <Typography variant="body2" color="text.secondary">
          Indicate ability or frequency for each.
        </Typography>
        {CLEANLINESS.map(({ key, label }) => (
          <TextRow key={key} label={label} k={key} fields={fields} setF={setF} />
        ))}
        <TextRow label="Other information" k="cleanliness_other" fields={fields} setF={setF} />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Feeding</Typography>
        <TextRow
          label="What types of difficulties do you experience while feeding your child?"
          k="feeding_difficulties" fields={fields} setF={setF} multiline
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please answer Yes or No for each:
        </Typography>
        {FEEDING_BEHAVIOR.map(({ key, label }) => (
          <YesNoRow key={key} label={label} k={`feed_${key}_yn`} fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        ))}
        <TextRow label="Comments" k="behavior_comments" fields={fields} setF={setF} multiline />

        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>Drinking — check all that apply:</Typography>
        <CheckboxGroup items={DRINKING} fields={fields} setF={setF} cols={5} />
        <TextRow label='If "Other", specify' k="drink_other_specify" fields={fields} setF={setF} />

        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>Eating — check all that apply:</Typography>
        <CheckboxGroup items={EATING} fields={fields} setF={setF} cols={3} />

        <TextRow label="My child is allergic to the following foods" k="allergic_foods" fields={fields} setF={setF} />
        <TextRow label="My child dislikes the following foods" k="disliked_foods" fields={fields} setF={setF} />
        <TextRow label="My child prefers the following foods" k="preferred_foods" fields={fields} setF={setF} />
        <TextRow label="My child is restricted from eating the following foods" k="restricted_foods" fields={fields} setF={setF} />
        <TextRow
          label="List foods child can eat with consistency (liquids, pureed, mashed, chopped, pieces)"
          k="food_consistency" fields={fields} setF={setF} multiline minRows={3}
        />
        <TextRow label="Easiest foods to eat (examples)" k="easiest_foods" fields={fields} setF={setF} />
        <TextRow label="Hardest foods to eat (examples)" k="hardest_foods" fields={fields} setF={setF} />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Sleep habits
          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
        </Typography>
        <TextRow label="Time child goes to bed" k="bedtime" fields={fields} setF={setF} />
        <YesNoRow label="Takes a nap during the day?" k="naps_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <YesNoRow label="Sleeps through the night?" k="sleeps_through_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <TwoCol>
          <TextRow label="Where does child sleep?" k="where_sleeps" fields={fields} setF={setF} />
          <TextRow label="With whom?" k="with_whom" fields={fields} setF={setF} />
        </TwoCol>
        <YesNoRow label="Any toilet accidents?" k="toilet_accidents_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <TextRow label="Any nightmares or peculiarities" k="nightmares" fields={fields} setF={setF} multiline />
      </SectionAccordion>

      <SectionAccordion
        title="V. Communication"
        sectionId="communication"
        expanded={!!expandedSections.communication}
        onExpandedChange={handleSectionExpanded}
      >
        <Typography variant="body2">How does child communicate?</Typography>
        <CheckboxGroup items={COMM_METHODS} fields={fields} setF={setF} cols={2} />
        {COMM_YN.map(({ prefix, label }) => (
          <YesNoRow key={prefix} label={label} k={`${prefix}_yn`} fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        ))}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Speech therapy outside school (RSA)</Typography>
        <TwoCol>
          <TextRow label="From whom" k="rsa_from_whom" fields={fields} setF={setF} />
          <TextRow label="How often" k="rsa_how_often" fields={fields} setF={setF} />
        </TwoCol>
      </SectionAccordion>

      <SectionAccordion
        title="VI. Skills"
        sectionId="skills"
        expanded={!!expandedSections.skills}
        onExpandedChange={handleSectionExpanded}
      >
        <TextRow label="How does your child use leisure time?" k="leisure" fields={fields} setF={setF} multiline />
        <TextRow
          label="Is there anything the child fears or becomes upset by?"
          k="fears" fields={fields} setF={setF} multiline
        />
        <YesNoRow label="Does child go outside unattended?" k="goes_outside_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Play habits</Typography>
        {PLAY_HABITS.map(({ prefix, label }) => (
          <YesNoRow key={prefix} label={label} k={`${prefix}_yn`} fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        ))}
        <TextRow label="Child's favorite activities" k="favorite_activities" fields={fields} setF={setF} multiline />
      </SectionAccordion>

      <SectionAccordion
        title="VII. Emotional Behavior"
        sectionId="emotional"
        expanded={!!expandedSections.emotional}
        onExpandedChange={handleSectionExpanded}
      >
        <Typography variant="body2" color="text.secondary">
          Check any that describe your child:
        </Typography>
        <CheckboxGroup items={EMOTIONAL} fields={fields} setF={setF} cols={4} />

        <YesNoRow
          label="Does your child seek attention all the time?"
          k="seek_attention_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />
        <TextRow label="How?" k="how_seek" fields={fields} setF={setF} />
        <TextRow label="What calms child?" k="what_calms" fields={fields} setF={setF} multiline />
        <YesNoRow label="Does he/she share attention?" k="share_attention_yn" fields={fields} setF={setF} fieldErrors={fieldErrors} clearFieldError={clearFieldError} readOnly={readOnly} />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>How does your child react to:</Typography>
        <TextRow label="Transitions" k="react_transitions" fields={fields} setF={setF} multiline />
        <TextRow label="New environments" k="react_new_env" fields={fields} setF={setF} multiline />
      </SectionAccordion>
    </Box>
    </Fa12FieldContext.Provider>
  );
}
